import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';

import { WorkScheduleService } from '@services/work-schedule/work-schedule.service';
import { CalendarPreviewResult } from '@services/work-schedule/model';
import { UserdataService } from '@services/userdata/userdata.service';
import { UtilsService } from '@services/utils/utils.service';
import { TranslateService } from '@ngx-translate/core';
import { Events } from '@services/events/events.service';

/**
 * Time display mode for duration displays
 * - Clock: Show only clock time (default)
 * - Work: Show only work time
 * - Both: Show both, e.g., "18 hours (2.5 work hrs)"
 */
export enum TimeDisplayMode {
  Clock = 'clock',
  Work = 'work',
  Both = 'both'
}

/**
 * Duration result with both clock and work time
 */
export interface DurationResult {
  clockSeconds: number;
  workSeconds: number;
  formatted: string;
}

/**
 * Cached schedule data for client-side work time calculation
 */
interface CachedScheduleData {
  locationID: number;
  workingDays: string[];  // YYYY-MM-DD format
  daysOff: string[];      // YYYY-MM-DD format
  dayDetails: { [date: string]: any };
  fetchedAt: number;
  startDate: string;      // YYYY-MM-DD format - start of cached range
  endDate: string;        // YYYY-MM-DD format - end of cached range
}

const PREFERENCE_KEY = 'timeDisplayMode';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

@Injectable({
  providedIn: 'root'
})
export class TimeDisplayService {
  private scheduleCache: Map<number, CachedScheduleData> = new Map();
  private pendingFetches: Map<number, Promise<CachedScheduleData | null>> = new Map();

  constructor(
    private logger: NGXLogger,
    private workScheduleService: WorkScheduleService,
    private userdataService: UserdataService,
    private utilsService: UtilsService,
    private translate: TranslateService,
    private events: Events
  ) {}

  /**
   * Get the current time display mode from user preferences
   */
  public getMode(): TimeDisplayMode {
    const stored = this.userdataService.getPreference(PREFERENCE_KEY);
    if (
      stored === TimeDisplayMode.Clock ||
      stored === TimeDisplayMode.Work ||
      stored === TimeDisplayMode.Both
    ) {
      return stored;
    }
    return TimeDisplayMode.Clock; // Default
  }

  /**
   * Set the time display mode and save to user preferences
   */
  public setMode(mode: TimeDisplayMode): Promise<any> {
    return this.userdataService.savePreference(PREFERENCE_KEY, mode);
  }

  /**
   * Format a duration based on the current display mode
   * @param clockSeconds Clock time duration in seconds
   * @param workSeconds Work time duration in seconds (optional, uses clock if not provided)
   * @returns Formatted duration string
   */
  public formatDuration(clockSeconds: number, workSeconds?: number): string {
    const mode = this.getMode();
    const effectiveWorkSeconds = workSeconds ?? clockSeconds;

    switch (mode) {
      case TimeDisplayMode.Work:
        return this.formatSeconds(effectiveWorkSeconds);
      case TimeDisplayMode.Both:
        return this.formatBothModes(clockSeconds, effectiveWorkSeconds);
      case TimeDisplayMode.Clock:
      default:
        return this.formatSeconds(clockSeconds);
    }
  }

  /**
   * Format duration for "both" mode: "18 hours (2.5 work hrs)"
   */
  private formatBothModes(clockSeconds: number, workSeconds: number): string {
    const clockFormatted = this.formatSeconds(clockSeconds);
    const workHours = (workSeconds / 3600).toFixed(1);
    const workHrsLabel = this.translate.instant('WORK_SCHEDULE.work_hrs');
    return `${clockFormatted} (${workHours} ${workHrsLabel})`;
  }

  /**
   * Format seconds to HH:MM:SS or human-readable duration
   */
  private formatSeconds(seconds: number): string {
    return this.utilsService.secsToDuration(seconds);
  }

  /**
   * Calculate work time since a timestamp for a given location
   * Uses cached work schedule data for client-side calculation
   * @param startTimestamp Unix timestamp in seconds
   * @param locationID Location ID to get work schedule for
   * @returns Work time in seconds (falls back to clock time if no schedule)
   */
  public async calculateWorkTimeSince(startTimestamp: number, locationID: number): Promise<number> {
    const nowSeconds = Math.floor(Date.now() / 1000);
    const clockSeconds = nowSeconds - startTimestamp;

    if (!locationID || locationID <= 0) {
      return clockSeconds;
    }

    try {
      const scheduleData = await this.getCachedSchedule(locationID, startTimestamp, nowSeconds);
      if (!scheduleData) {
        return clockSeconds;
      }

      return this.calculateWorkTimeFromSchedule(startTimestamp, nowSeconds, scheduleData);
    } catch (error) {
      this.logger.warn('[TimeDisplayService] Error calculating work time, using clock time:', this.safeErrorMessage(error));
      return clockSeconds;
    }
  }

  /**
   * Synchronous version that returns cached data or clock time
   * Use this when you need immediate results without async
   */
  public calculateWorkTimeSinceSync(startTimestamp: number, locationID: number): number {
    const nowSeconds = Math.floor(Date.now() / 1000);
    const clockSeconds = nowSeconds - startTimestamp;

    if (!locationID || locationID <= 0) {
      return clockSeconds;
    }

    const requestedStartDate = this.formatDate(startTimestamp);
    const requestedEndDate = this.formatDate(nowSeconds);

    const cached = this.scheduleCache.get(locationID);
    if (!cached || this.isCacheStale(cached)) {
      // Trigger async fetch for next time, return clock time now
      this.getCachedSchedule(locationID, startTimestamp, nowSeconds).catch(() => {});
      return clockSeconds;
    }

    // Verify cached data covers the requested date range
    if (cached.startDate > requestedStartDate || cached.endDate < requestedEndDate) {
      // Cache doesn't cover the full range - trigger fetch and return clock time
      this.getCachedSchedule(locationID, startTimestamp, nowSeconds).catch(() => {});
      return clockSeconds;
    }

    const workSeconds = this.calculateWorkTimeFromSchedule(startTimestamp, nowSeconds, cached);
    // If work time is 0 but clock time is significant, fall back to clock time
    // This handles cases where schedule data doesn't cover the date range
    if (workSeconds === 0 && clockSeconds > 3600) {
      return clockSeconds;
    }
    return workSeconds;
  }

  /**
   * Work time between two timestamps (seconds) from cached schedule data. Falls back to clock
   * time - and triggers a fetch - when the cache does not cover the range yet.
   *
   * Unlike calculateWorkTimeSinceSync, a 0 is returned as 0: the range is checked for coverage,
   * so 0 means the range really held no work time (a night, a weekend).
   */
  public calculateWorkTimeBetweenSync(startTimestamp: number, endTimestamp: number, locationID: number): number {
    const clockSeconds = Math.max(0, endTimestamp - startTimestamp);

    if (!locationID || locationID <= 0 || clockSeconds === 0) {
      return clockSeconds;
    }

    if (!this.isScheduleCachedSync(startTimestamp, endTimestamp, locationID)) {
      this.getCachedSchedule(locationID, startTimestamp, endTimestamp).catch(() => {});
      return clockSeconds;
    }

    return this.calculateWorkTimeFromSchedule(startTimestamp, endTimestamp, this.scheduleCache.get(locationID));
  }

  /**
   * Whether calculateWorkTimeBetweenSync can answer this range from the schedule (always true
   * without a location, where work time is clock time). False means it would fall back to clock
   * time, so a caller should not keep that result.
   */
  public isScheduleCachedSync(startTimestamp: number, endTimestamp: number, locationID: number): boolean {
    if (!locationID || locationID <= 0) {
      return true;
    }
    const cached = this.scheduleCache.get(locationID);
    return !!cached && !this.isCacheStale(cached) &&
      cached.startDate <= this.formatDate(startTimestamp) && cached.endDate >= this.formatDate(endTimestamp);
  }

  /**
   * Get cached schedule data or fetch if needed
   */
  private async getCachedSchedule(
    locationID: number,
    startTimestamp: number,
    endTimestamp: number
  ): Promise<CachedScheduleData | null> {
    const requestedStartDate = this.formatDate(startTimestamp);
    const requestedEndDate = this.formatDate(endTimestamp);

    const cached = this.scheduleCache.get(locationID);
    if (cached && !this.isCacheStale(cached)) {
      // Verify the cached data covers the requested date range
      if (cached.startDate <= requestedStartDate && cached.endDate >= requestedEndDate) {
        return cached;
      }
      // Cache doesn't cover the full range - need to fetch with expanded range
      this.logger.debug('[TimeDisplayService] Cache range insufficient, fetching expanded range');
    }

    // Check if there's already a pending fetch
    const pending = this.pendingFetches.get(locationID);
    if (pending) {
      return pending;
    }

    // Fetch new data - expand range to include existing cache if applicable
    let fetchStartTimestamp = startTimestamp;
    let fetchEndTimestamp = endTimestamp;
    if (cached && cached.startDate && cached.endDate) {
      // Expand to cover both old cache and new request
      const cachedStartTimestamp = new Date(cached.startDate + 'T00:00:00').getTime() / 1000;
      const cachedEndTimestamp = new Date(cached.endDate + 'T23:59:59').getTime() / 1000;
      fetchStartTimestamp = Math.min(startTimestamp, cachedStartTimestamp);
      fetchEndTimestamp = Math.max(endTimestamp, cachedEndTimestamp);
    }

    const fetchPromise = this.fetchScheduleData(locationID, fetchStartTimestamp, fetchEndTimestamp);
    this.pendingFetches.set(locationID, fetchPromise);

    try {
      const result = await fetchPromise;
      return result;
    } finally {
      this.pendingFetches.delete(locationID);
    }
  }

  /**
   * Fetch schedule data from the backend
   */
  private async fetchScheduleData(
    locationID: number,
    startTimestamp: number,
    endTimestamp: number
  ): Promise<CachedScheduleData | null> {
    try {
      // Get the location's default schedules
      await this.workScheduleService.getWorkSchedules();
      const defaults = this.workScheduleService.getLocationDefaultSchedules(locationID);

      // Get calendar preview for the date range
      // Pass 0 for scheduleIDs to let backend resolve inheritance from location/subscriber defaults
      const startDate = this.formatDate(startTimestamp);
      const endDate = this.formatDate(endTimestamp);

      const preview: CalendarPreviewResult = await this.workScheduleService.getCalendarPreviewForLocation(
        defaults.workDaysScheduleID || 0,
        defaults.daysOffScheduleID || 0,
        startDate,
        endDate,
        locationID
      );

      // If no working days returned, the location has no effective schedule
      if (!preview.workingDays || preview.workingDays.length === 0) {
        this.logger.warn('[TimeDisplayService] No working days found for location:', locationID);
        return null;
      }

      const cached: CachedScheduleData = {
        locationID,
        workingDays: preview.workingDays,
        daysOff: preview.daysOff,
        dayDetails: preview.dayDetails,
        fetchedAt: Date.now(),
        startDate,
        endDate
      };

      this.scheduleCache.set(locationID, cached);
      this.events.publish('ccs:scheduleLoaded', locationID);
      return cached;
    } catch (error) {
      this.logger.warn('[TimeDisplayService] Failed to fetch schedule data:', this.safeErrorMessage(error));
      return null;
    }
  }

  /**
   * Calculate work time from cached schedule data
   */
  private calculateWorkTimeFromSchedule(
    startTimestamp: number,
    endTimestamp: number,
    scheduleData: CachedScheduleData
  ): number {
    const workingDaysSet = new Set(scheduleData.workingDays);
    const daysOffSet = new Set(scheduleData.daysOff);

    let workSeconds = 0;
    const startDate = new Date(startTimestamp * 1000);
    const endDate = new Date(endTimestamp * 1000);

    // Iterate through each day
    const currentDate = new Date(startDate);
    currentDate.setHours(0, 0, 0, 0);

    while (currentDate <= endDate) {
      const dateStr = this.formatDateFromDate(currentDate);

      // Check if this is a working day
      if (daysOffSet.has(dateStr)) {
        // Day off - no work time
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }

      if (!workingDaysSet.has(dateStr)) {
        // Not a working day - no work time (weekend or unscheduled day)
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }

      // Working day - calculate work time for this day
      const dayDetails = scheduleData.dayDetails?.[dateStr];
      if (dayDetails && dayDetails.entries && dayDetails.entries.length > 0) {
        // Has specific work periods
        for (const entryWrapper of dayDetails.entries) {
          // Entry data is nested inside entry.entry
          const entry = entryWrapper.entry || entryWrapper;
          const periodStart = this.parseTimeToSeconds(entry.startTime, currentDate);
          const periodEnd = this.parseTimeToSeconds(entry.endTime, currentDate);

          // Skip entries with missing start/end times
          if (periodStart === null || periodEnd === null) {
            continue;
          }

          // Calculate overlap with the time range
          const overlapStart = Math.max(startTimestamp, periodStart);
          const overlapEnd = Math.min(endTimestamp, periodEnd);

          if (overlapEnd > overlapStart) {
            workSeconds += overlapEnd - overlapStart;
          }
        }
      } else {
        // Working day but no specific entries - count full day overlap
        const dayStart = Math.max(startTimestamp, this.getStartOfDay(currentDate));
        const dayEnd = Math.min(endTimestamp, this.getEndOfDay(currentDate));
        if (dayEnd > dayStart) {
          // Assume standard 8-hour work day if no specific entries
          const hoursInRange = (dayEnd - dayStart) / 3600;
          workSeconds += Math.min(hoursInRange, 8) * 3600;
        }
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return workSeconds;
  }

  /**
   * Preload schedules for multiple locations
   */
  public async preloadSchedulesForLocations(locationIDs: number[]): Promise<void> {
    const now = Math.floor(Date.now() / 1000);
    const thirtyDaysAgo = now - (30 * 24 * 60 * 60);

    const promises = locationIDs
      .filter(id => id > 0 && !this.scheduleCache.has(id))
      .map(id => this.getCachedSchedule(id, thirtyDaysAgo, now).catch(() => null));

    await Promise.all(promises);
  }

  /**
   * Clear the schedule cache (call on SSE cache invalidation)
   */
  public clearCache(): void {
    this.scheduleCache.clear();
  }

  /**
   * Clear cache for a specific location
   */
  public clearLocationCache(locationID: number): void {
    this.scheduleCache.delete(locationID);
  }

  // ========== Helper Methods ==========

  private isCacheStale(cached: CachedScheduleData): boolean {
    return Date.now() - cached.fetchedAt > CACHE_TTL_MS;
  }

  private formatDate(timestamp: number): string {
    const date = new Date(timestamp * 1000);
    return this.formatDateFromDate(date);
  }

  private formatDateFromDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private getStartOfDay(date: Date): number {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return Math.floor(d.getTime() / 1000);
  }

  private getEndOfDay(date: Date): number {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return Math.floor(d.getTime() / 1000);
  }

  private safeErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    if (error === undefined || error === null) {
      return String(error);
    }
    try {
      return JSON.stringify(error) ?? '[non-serializable error]';
    } catch (e) {
      return '[non-serializable error]';
    }
  }

  private parseTimeToSeconds(timeStr: string, date: Date): number | null {
    // timeStr format: "HH:MM" or "HH:MM:SS"
    if (!timeStr || typeof timeStr !== 'string') {
      return null;
    }
    const parts = timeStr.split(':').map(Number);
    const d = new Date(date);
    d.setHours(parts[0] || 0, parts[1] || 0, parts[2] || 0, 0);
    return Math.floor(d.getTime() / 1000);
  }
}
